import { CheckIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

const baseBubbleStyles = 'border rounded-full h-20 w-20 block relative mx-auto my-0 z-10';

const bubbleStatusStyles = {
    done: 'border-mango bg-mango ',
    'in progress': 'border-black bg-off-white',
    upcoming: 'border-black bg-white',
};

const baseDividerStyles = 'absolute block bg-black';

const dividerStyles = {
    vertical: 'ml-10',
    horizontal: 'mt-10',
};

const stepStyles = {
    vertical: 'py-10',
    horizontal: 'flex flex-col',
};

function Stepper({ containerWidth, orientation, size, stackedText, steps }) {
    const horizontal = orientation === 'horizontal';
    const vertical = orientation === 'vertical';

    const getStepText = ({ name, subtext }) =>
        stackedText ? (
            <>
                <p className="font-grotesk-bold">{name}</p>
                <p>{subtext}</p>
            </>
        ) : (
            <>
                <span className="font-grotesk-bold">{name}</span> {subtext && name && vertical && '| '}
                <span>{subtext}</span>
            </>
        );

    return (
        <div className="flex">
            <div
                className={clsx('relative flex justify-between', { 'mr-20 flex-col': vertical })}
                style={{ width: horizontal ? containerWidth : 'auto' }}
            >
                {steps.map((step, i) => (
                    <div
                        key={step.id}
                        className={clsx('flex', {
                            'flex-col': horizontal,
                            'gap-15': size === 'medium',
                            'gap-10': size === 'small',
                        })}
                    >
                        <div
                            className={clsx(
                                { 'self-start': i === 0 && vertical },
                                { 'self-end': i === steps.length - 1 && vertical },
                                { 'self-center': i !== 0 && i !== steps.length - 1 && vertical },
                                { 'ml-auto': i === steps.length - 1 && horizontal },
                                { 'mr-auto': i === 0 && horizontal }
                            )}
                        >
                            <span
                                data-testid={step.id}
                                className={clsx(
                                    baseBubbleStyles,
                                    bubbleStatusStyles[step.status ? step.status : 'upcoming']
                                )}
                            >
                                {step.status === 'done' && (
                                    <CheckIcon className="absolute left-[3px] top-[3px] w-[12px] stroke-[4px] text-black" />
                                )}
                                {step.status === 'in progress' && (
                                    <span className="absolute left-[6px] top-[6px] h-[6px] w-[6px] rounded-full border border-black bg-black" />
                                )}
                            </span>
                        </div>
                        <div
                            key={step.id}
                            className={clsx(
                                { 'pb-10 pt-0': i === 0 && vertical },
                                { 'relative top-[2px] pb-0 pt-10': i === steps.length - 1 && vertical },
                                { 'text-left': i === 0 && horizontal },
                                { 'text-right': i === steps.length - 1 && horizontal },
                                { 'text-center': i !== 0 && i !== steps.length - 1 && horizontal },
                                stepStyles[orientation]
                            )}
                        >
                            {step.link && step.status === 'done' ? (
                                <Link className="link !text-black" to={step.link}>
                                    {getStepText({ ...step })}
                                </Link>
                            ) : (
                                getStepText({ ...step })
                            )}
                        </div>
                    </div>
                ))}
                <span
                    className={clsx(baseDividerStyles, dividerStyles[orientation])}
                    style={{
                        width: horizontal ? '100%' : `1px`,
                        height: horizontal ? `1px` : '100%',
                    }}
                />
            </div>
        </div>
    );
}

Stepper.propTypes = {
    containerWidth: PropTypes.string,
    orientation: PropTypes.oneOf(['horizontal', 'vertical']),
    size: PropTypes.oneOf(['medium', 'small']),
    stackedText: PropTypes.bool,
    steps: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            link: PropTypes.string,
            name: PropTypes.string.isRequired,
            status: PropTypes.oneOf(['done', 'in progress', 'upcoming']),
            subtext: PropTypes.string,
        })
    ).isRequired,
};

Stepper.defaultProps = {
    containerWidth: '330px',
    orientation: 'horizontal',
    size: 'medium',
    stackedText: false,
};

export default Stepper;
